/**
 * DemoVideoPlayer Component
 *
 * A responsive video player for the product demo video.
 * Features:
 * - Autoplay with muted audio (browser requirement)
 * - Loop playback
 * - Rounded corners with shadow for premium feel
 * - Mac-style window chrome to match Screen Studio recording
 * - Responsive sizing
 */

import { useRef, useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface DemoVideoPlayerProps {
  className?: string;
  /** Whether the video is currently visible (for optimization) */
  isVisible?: boolean;
  /** Video source URL */
  src?: string;
  /** Called when video ends (for non-loop mode) */
  onEnded?: () => void;
  /** Whether to show play/pause controls on hover */
  showControls?: boolean;
}

const DEFAULT_VIDEO_SRC = 'https://db-public-reports.s3.us-east-2.amazonaws.com/Vettis+v0.mp4';

export function DemoVideoPlayer({
  className,
  isVisible = true,
  src = DEFAULT_VIDEO_SRC,
  onEnded,
  showControls = false,
}: DemoVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);

  // Handle visibility changes - pause when not visible to save resources
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (isVisible && isLoaded) {
      video.play().catch(() => {
        // Autoplay may be blocked by browser
        console.log('Video autoplay blocked by browser');
      });
    } else {
      video.pause();
    }
  }, [isVisible, isLoaded]);

  const handleLoadedData = () => {
    setIsLoaded(true);
    setIsPlaying(true);
  };

  const handlePlay = () => setIsPlaying(true);
  const handlePause = () => setIsPlaying(false);

  const togglePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
    } else {
      video.play();
    }
  };

  return (
    <div className={cn('relative group w-full flex items-center justify-center', className)}>
      {/* Clean video frame - no title bar, sized to video aspect ratio */}
      <div className="relative w-full flex items-center justify-center">
        {/* Video wrapper with rounded corners and shadow - aspect-video to match video content */}
        <div className="relative rounded-2xl overflow-hidden bg-black shadow-2xl shadow-black/60 border border-border-subtle w-full aspect-video">
          {/* Loading placeholder */}
          {!isLoaded && (
            <div className="absolute inset-0 flex items-center justify-center bg-black z-10">
              <div className="w-8 h-8 border-2 border-verdict-primary/30 border-t-verdict-primary rounded-full animate-spin" />
            </div>
          )}

          <video
            ref={videoRef}
            src={src}
            className={cn(
              'w-full h-full object-cover transition-opacity duration-500',
              isLoaded ? 'opacity-100' : 'opacity-0'
            )}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            onLoadedData={handleLoadedData}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={onEnded}
          />

          {/* Play/Pause overlay - shown on hover if controls enabled */}
          {showControls && (
            <button
              onClick={togglePlayPause}
              className={cn(
                'absolute inset-0 flex items-center justify-center',
                'bg-black/0 hover:bg-black/30 active:bg-black/40 transition-colors duration-300',
                'opacity-0 group-hover:opacity-100 focus:opacity-100'
              )}
            >
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                {isPlaying ? (
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-text-primary" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                  </svg>
                ) : (
                  <svg className="w-6 h-6 sm:w-8 sm:h-8 text-text-primary ml-0.5 sm:ml-1" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

